import {
  CoreLanguageType,
  LanguageType,
  proxyLanguageRecord,
} from 'shared/src/types/languages.type';

type SitemapUiContent = {
  // Main navigation buttons
  expandAll: string;
  collapseAll: string;
  expand: string;
  collapse: string;

  // Labels and counts
  categories: string;
  maxDepth: string;
  mainCategories: string;
  totalSubcategories: string;
  products: string;
  empty: string;
  explore: string;

  // Navigation
  backToSitemap: string;
  siteNavigation: string;

  // Status indicators
  deepStructure: string;

  // Empty states
  noSubcategoriesFound: string;
  noSubcategoriesDescription: string;
  noCategoriesFound: string;
  noCategoriesDescription: string;

  // Error states
  somethingWentWrong: string;
  errorLoadingCategory: string;
  tryAgain: string;

  // Descriptions and hints
  exploreDescription: string;
  clickToExplore: string;

  // Development
  debugInfo: string;

  // Metadata (for generateMetadata functions)
  sitemapCategory: string;
  comprehensiveSitemapDescription: string;
  categoryNotFound: string;
  categoryNotFoundDescription: string;
  completeCategoryStructure: string;
  navigateSubcategories: string;
};

const sitemapUiContentCore: Record<CoreLanguageType, SitemapUiContent> = {
  // English
  en: {
    // Main navigation buttons
    expandAll: 'Expand All',
    collapseAll: 'Collapse All',
    expand: 'Expand',
    collapse: 'Collapse',

    // Labels and counts
    categories: 'Categories',
    maxDepth: 'Max Depth',
    mainCategories: 'Main Categories',
    totalSubcategories: 'Total Subcategories',
    products: 'Products',
    empty: 'Empty',
    explore: 'Explore',

    // Navigation
    backToSitemap: 'Back to Sitemap',
    siteNavigation: 'Site Navigation',

    // Status indicators
    deepStructure: 'Deep structure',

    // Empty states
    noSubcategoriesFound: 'No subcategories found',
    noSubcategoriesDescription:
      "This category doesn't have any subcategories yet. It might be a product category or still being built.",
    noCategoriesFound: 'No categories found',
    noCategoriesDescription: 'The sitemap is currently being built. Please check back later.',

    // Error states
    somethingWentWrong: 'Something went wrong',
    errorLoadingCategory: 'We encountered an error while loading the sitemap category.',
    tryAgain: 'Try again',

    // Descriptions and hints
    exploreDescription:
      "Explore our comprehensive catalog organized into categories and subcategories. Find exactly what you're looking for in our structured site map.",
    clickToExplore: 'Click on any category to explore its subcategories and products',

    // Development
    debugInfo: 'Debug Info (Development)',

    // Metadata
    sitemapCategory: 'Sitemap Category',
    comprehensiveSitemapDescription: 'Browse our comprehensive sitemap structure.',
    categoryNotFound: 'Category Not Found',
    categoryNotFoundDescription: 'The requested sitemap category could not be found.',
    completeCategoryStructure: 'Complete Category Structure',
    navigateSubcategories: 'Navigate through subcategories',
  },

  // French
  fr: {
    // Main navigation buttons
    expandAll: 'Tout développer',
    collapseAll: 'Tout réduire',
    expand: 'Développer',
    collapse: 'Réduire',

    // Labels and counts
    categories: 'Catégories',
    maxDepth: 'Profondeur max',
    mainCategories: 'Catégories principales',
    totalSubcategories: 'Total des sous-catégories',
    products: 'Produits',
    empty: 'Vide',
    explore: 'Explorer',

    // Navigation
    backToSitemap: 'Retour au plan du site',
    siteNavigation: 'Navigation du site',

    // Status indicators
    deepStructure: 'Structure profonde',

    // Empty states
    noSubcategoriesFound: 'Aucune sous-catégorie trouvée',
    noSubcategoriesDescription:
      "Cette catégorie n'a pas encore de sous-catégories. Il pourrait s'agir d'une catégorie de produits ou encore en construction.",
    noCategoriesFound: 'Aucune catégorie trouvée',
    noCategoriesDescription: 'Le plan du site est actuellement en construction. Veuillez revenir plus tard.',

    // Error states
    somethingWentWrong: "Quelque chose s'est mal passé",
    errorLoadingCategory: 'Nous avons rencontré une erreur lors du chargement de la catégorie du plan du site.',
    tryAgain: 'Réessayer',

    // Descriptions and hints
    exploreDescription:
      'Explorez notre catalogue complet organisé en catégories et sous-catégories. Trouvez exactement ce que vous cherchez dans notre plan de site structuré.',
    clickToExplore: "Cliquez sur n'importe quelle catégorie pour explorer ses sous-catégories et produits",

    // Development
    debugInfo: 'Infos de débogage (Développement)',

    // Metadata
    sitemapCategory: 'Catégorie du plan du site',
    comprehensiveSitemapDescription: 'Parcourez notre structure complète du plan du site.',
    categoryNotFound: 'Catégorie non trouvée',
    categoryNotFoundDescription: 'La catégorie du plan du site demandée est introuvable.',
    completeCategoryStructure: 'Structure complète des catégories',
    navigateSubcategories: 'Naviguer à travers les sous-catégories',
  },

  // Spanish
  es: {
    // Main navigation buttons
    expandAll: 'Expandir todo',
    collapseAll: 'Contraer todo',
    expand: 'Expandir',
    collapse: 'Contraer',

    // Labels and counts
    categories: 'Categorías',
    maxDepth: 'Profundidad máx.',
    mainCategories: 'Categorías principales',
    totalSubcategories: 'Total de subcategorías',
    products: 'Productos',
    empty: 'Vacío',
    explore: 'Explorar',

    // Navigation
    backToSitemap: 'Volver al mapa del sitio',
    siteNavigation: 'Navegación del sitio',

    // Status indicators
    deepStructure: 'Estructura profunda',

    // Empty states
    noSubcategoriesFound: 'No se encontraron subcategorías',
    noSubcategoriesDescription:
      'Esta categoría aún no tiene subcategorías. Podría ser una categoría de productos o estar aún en construcción.',
    noCategoriesFound: 'No se encontraron categorías',
    noCategoriesDescription: 'El mapa del sitio se está construyendo actualmente. Por favor, vuelva más tarde.',

    // Error states
    somethingWentWrong: 'Algo salió mal',
    errorLoadingCategory: 'Encontramos un error al cargar la categoría del mapa del sitio.',
    tryAgain: 'Intentar de nuevo',

    // Descriptions and hints
    exploreDescription:
      'Explore nuestro catálogo integral organizado en categorías y subcategorías. Encuentre exactamente lo que busca en nuestro mapa de sitio estructurado.',
    clickToExplore: 'Haga clic en cualquier categoría para explorar sus subcategorías y productos',

    // Development
    debugInfo: 'Info de depuración (Desarrollo)',

    // Metadata
    sitemapCategory: 'Categoría del mapa del sitio',
    comprehensiveSitemapDescription: 'Explore nuestra estructura completa del mapa del sitio.',
    categoryNotFound: 'Categoría no encontrada',
    categoryNotFoundDescription: 'No se pudo encontrar la categoría del mapa del sitio solicitada.',
    completeCategoryStructure: 'Estructura completa de categorías',
    navigateSubcategories: 'Navegar a través de subcategorías',
  },

  // German
  de: {
    // Main navigation buttons
    expandAll: 'Alle erweitern',
    collapseAll: 'Alle reduzieren',
    expand: 'Erweitern',
    collapse: 'Reduzieren',

    // Labels and counts
    categories: 'Kategorien',
    maxDepth: 'Max. Tiefe',
    mainCategories: 'Hauptkategorien',
    totalSubcategories: 'Gesamte Unterkategorien',
    products: 'Produkte',
    empty: 'Leer',
    explore: 'Erkunden',

    // Navigation
    backToSitemap: 'Zurück zur Sitemap',
    siteNavigation: 'Site-Navigation',

    // Status indicators
    deepStructure: 'Tiefe Struktur',

    // Empty states
    noSubcategoriesFound: 'Keine Unterkategorien gefunden',
    noSubcategoriesDescription:
      'Diese Kategorie hat noch keine Unterkategorien. Es könnte eine Produktkategorie sein oder noch im Aufbau befindlich.',
    noCategoriesFound: 'Keine Kategorien gefunden',
    noCategoriesDescription: 'Die Sitemap wird derzeit erstellt. Bitte schauen Sie später noch einmal vorbei.',

    // Error states
    somethingWentWrong: 'Etwas ist schief gelaufen',
    errorLoadingCategory: 'Beim Laden der Sitemap-Kategorie ist ein Fehler aufgetreten.',
    tryAgain: 'Erneut versuchen',

    // Descriptions and hints
    exploreDescription:
      'Erkunden Sie unseren umfassenden Katalog, der in Kategorien und Unterkategorien organisiert ist. Finden Sie genau das, wonach Sie in unserer strukturierten Sitemap suchen.',
    clickToExplore: 'Klicken Sie auf eine beliebige Kategorie, um ihre Unterkategorien und Produkte zu erkunden',

    // Development
    debugInfo: 'Debug-Informationen (Entwicklung)',

    // Metadata
    sitemapCategory: 'Sitemap-Kategorie',
    comprehensiveSitemapDescription: 'Durchsuchen Sie unsere umfassende Sitemap-Struktur.',
    categoryNotFound: 'Kategorie nicht gefunden',
    categoryNotFoundDescription: 'Die angeforderte Sitemap-Kategorie konnte nicht gefunden werden.',
    completeCategoryStructure: 'Vollständige Kategoriestruktur',
    navigateSubcategories: 'Durch Unterkategorien navigieren',
  },

  // Italian
  it: {
    // Main navigation buttons
    expandAll: 'Espandi tutto',
    collapseAll: 'Comprimi tutto',
    expand: 'Espandi',
    collapse: 'Comprimi',

    // Labels and counts
    categories: 'Categorie',
    maxDepth: 'Profondità massima',
    mainCategories: 'Categorie principali',
    totalSubcategories: 'Totale sottocategorie',
    products: 'Prodotti',
    empty: 'Vuoto',
    explore: 'Esplora',

    // Navigation
    backToSitemap: 'Torna alla mappa del sito',
    siteNavigation: 'Navigazione del sito',

    // Status indicators
    deepStructure: 'Struttura profonda',

    // Empty states
    noSubcategoriesFound: 'Nessuna sottocategoria trovata',
    noSubcategoriesDescription:
      'Questa categoria non ha ancora sottocategorie. Potrebbe essere una categoria di prodotti o ancora in costruzione.',
    noCategoriesFound: 'Nessuna categoria trovata',
    noCategoriesDescription: 'La mappa del sito è in fase di costruzione. Si prega di riprovare più tardi.',

    // Error states
    somethingWentWrong: 'Qualcosa è andato storto',
    errorLoadingCategory: 'Si è verificato un errore durante il caricamento della categoria della mappa del sito.',
    tryAgain: 'Riprova',

    // Descriptions and hints
    exploreDescription:
      'Esplora il nostro catalogo completo organizzato in categorie e sottocategorie. Trova esattamente quello che cerchi nella nostra mappa del sito strutturata.',
    clickToExplore: 'Clicca su qualsiasi categoria per esplorare le sue sottocategorie e prodotti',

    // Development
    debugInfo: 'Informazioni di debug (Sviluppo)',

    // Metadata
    sitemapCategory: 'Categoria della mappa del sito',
    comprehensiveSitemapDescription: 'Sfoglia la nostra struttura completa della mappa del sito.',
    categoryNotFound: 'Categoria non trovata',
    categoryNotFoundDescription: 'La categoria della mappa del sito richiesta non è stata trovata.',
    completeCategoryStructure: 'Struttura completa delle categorie',
    navigateSubcategories: 'Naviga tra le sottocategorie',
  },

  // Brazilian Portuguese
  'pt-BR': {
    // Main navigation buttons
    expandAll: 'Expandir tudo',
    collapseAll: 'Recolher tudo',
    expand: 'Expandir',
    collapse: 'Recolher',

    // Labels and counts
    categories: 'Categorias',
    maxDepth: 'Profundidade máx.',
    mainCategories: 'Categorias principais',
    totalSubcategories: 'Total de subcategorias',
    products: 'Produtos',
    empty: 'Vazio',
    explore: 'Explorar',

    // Navigation
    backToSitemap: 'Voltar ao mapa do site',
    siteNavigation: 'Navegação do site',

    // Status indicators
    deepStructure: 'Estrutura profunda',

    // Empty states
    noSubcategoriesFound: 'Nenhuma subcategoria encontrada',
    noSubcategoriesDescription:
      'Esta categoria ainda não tem subcategorias. Pode ser uma categoria de produtos ou ainda está sendo construída.',
    noCategoriesFound: 'Nenhuma categoria encontrada',
    noCategoriesDescription: 'O mapa do site está sendo construído. Por favor, volte mais tarde.',

    // Error states
    somethingWentWrong: 'Algo deu errado',
    errorLoadingCategory: 'Encontramos um erro ao carregar a categoria do mapa do site.',
    tryAgain: 'Tentar novamente',

    // Descriptions and hints
    exploreDescription:
      'Explore nosso catálogo abrangente organizado em categorias e subcategorias. Encontre exatamente o que você está procurando no nosso mapa do site estruturado.',
    clickToExplore: 'Clique em qualquer categoria para explorar suas subcategorias e produtos',

    // Development
    debugInfo: 'Informações de depuração (Desenvolvimento)',

    // Metadata
    sitemapCategory: 'Categoria do mapa do site',
    comprehensiveSitemapDescription: 'Navegue pela nossa estrutura completa do mapa do site.',
    categoryNotFound: 'Categoria não encontrada',
    categoryNotFoundDescription: 'A categoria do mapa do site solicitada não foi encontrada.',
    completeCategoryStructure: 'Estrutura completa de categorias',
    navigateSubcategories: 'Navegar pelas subcategorias',
  },

  // European Portuguese
  'pt-PT': {
    // Main navigation buttons
    expandAll: 'Expandir tudo',
    collapseAll: 'Recolher tudo',
    expand: 'Expandir',
    collapse: 'Recolher',

    // Labels and counts
    categories: 'Categorias',
    maxDepth: 'Profundidade máx.',
    mainCategories: 'Categorias principais',
    totalSubcategories: 'Total de subcategorias',
    products: 'Produtos',
    empty: 'Vazio',
    explore: 'Explorar',

    // Navigation
    backToSitemap: 'Voltar ao mapa do site',
    siteNavigation: 'Navegação do site',

    // Status indicators
    deepStructure: 'Estrutura profunda',

    // Empty states
    noSubcategoriesFound: 'Nenhuma subcategoria encontrada',
    noSubcategoriesDescription:
      'Esta categoria ainda não tem subcategorias. Pode ser uma categoria de produtos ou ainda está em construção.',
    noCategoriesFound: 'Nenhuma categoria encontrada',
    noCategoriesDescription: 'O mapa do site está a ser construído. Por favor, volte mais tarde.',

    // Error states
    somethingWentWrong: 'Algo correu mal',
    errorLoadingCategory: 'Ocorreu um erro ao carregar a categoria do mapa do site.',
    tryAgain: 'Tentar novamente',

    // Descriptions and hints
    exploreDescription:
      'Explore o nosso catálogo abrangente organizado em categorias e subcategorias. Encontre exatamente o que procura no nosso mapa do site estruturado.',
    clickToExplore: 'Clique em qualquer categoria para explorar as suas subcategorias e produtos',

    // Development
    debugInfo: 'Informações de depuração (Desenvolvimento)',

    // Metadata
    sitemapCategory: 'Categoria do mapa do site',
    comprehensiveSitemapDescription: 'Navegue pela nossa estrutura completa do mapa do site.',
    categoryNotFound: 'Categoria não encontrada',
    categoryNotFoundDescription: 'A categoria do mapa do site solicitada não foi encontrada.',
    completeCategoryStructure: 'Estrutura completa de categorias',
    navigateSubcategories: 'Navegar pelas subcategorias',
  },

  // Scottish Gaelic
  gd: {
    expandAll: 'Leudaich na h-Uile',
    collapseAll: 'Co-theannaich na h-Uile',
    expand: 'Leudaich',
    collapse: 'Co-theannaich',
    categories: 'Roinnean-seòrsa',
    maxDepth: 'Doimhneachd As Motha',
    mainCategories: 'Prìomh Roinnean-seòrsa',
    totalSubcategories: 'Fo-roinnean-seòrsa Gu Lèir',
    products: 'Batharan',
    empty: 'Falamh',
    explore: 'Rannsaich',
    backToSitemap: 'Air ais gu Mapa an Làraich',
    siteNavigation: 'Seòladh an Làraich',
    deepStructure: 'Structar domhainn',
    noSubcategoriesFound: 'Cha deach fo-roinnean-seòrsa a lorg',
    noSubcategoriesDescription:
      'Chan eil fo-roinnean-seòrsa aig an roinn-seòrsa seo fhathast. Dh\'fhaodadh gur e roinn-seòrsa bathair a th\' ann no tha e fhathast ga thogail.',
    noCategoriesFound: 'Cha deach roinnean-seòrsa a lorg',
    noCategoriesDescription: 'Tha mapa an làraich ga thogail an-dràsta. Thig air ais nas fhaide air adhart.',
    somethingWentWrong: 'Chaidh rudeigin ceàrr',
    errorLoadingCategory: 'Thachair mearachd fhad \'s a bha sinn a\' luchdachadh roinn-seòrsa mapa an làraich.',
    tryAgain: 'Feuch a-rithist',
    exploreDescription:
      'Rannsaich ar catalog coileanta a tha air a chur air dòigh ann an roinnean-seòrsa agus fo-roinnean-seòrsa. Lorg dìreach na tha thu a\' lorg nar mapa làraich structarach.',
    clickToExplore: 'Briog air roinn-seòrsa sam bith gus na fo-roinnean-seòrsa agus batharan aige a rannsachadh',
    debugInfo: 'Fiosrachadh Deasbaid (Leasachadh)',
    sitemapCategory: 'Roinn-seòrsa Mapa an Làraich',
    comprehensiveSitemapDescription: 'Brabhsaich structar coileanta mapa ar làraich.',
    categoryNotFound: 'Cha deach an Roinn-seòrsa a Lorg',
    categoryNotFoundDescription: 'Cha b\' urrainn dhuinn an roinn-seòrsa mapa làraich a chaidh iarraidh a lorg.',
    completeCategoryStructure: 'Structar Slàn nan Roinnean-seòrsa',
    navigateSubcategories: 'Seòl tro fho-roinnean-seòrsa',
  },

  // Swedish
  sv: {
    expandAll: 'Expandera alla',
    collapseAll: 'Minimera alla',
    expand: 'Expandera',
    collapse: 'Minimera',
    categories: 'Kategorier',
    maxDepth: 'Maxdjup',
    mainCategories: 'Huvudkategorier',
    totalSubcategories: 'Totalt antal underkategorier',
    products: 'Produkter',
    empty: 'Tomt',
    explore: 'Utforska',
    backToSitemap: 'Tillbaka till webbplatskartan',
    siteNavigation: 'Webbplatsnavigering',
    deepStructure: 'Djup struktur',
    noSubcategoriesFound: 'Inga underkategorier hittades',
    noSubcategoriesDescription:
      'Denna kategori har inga underkategorier ännu. Det kan vara en produktkategori eller så håller den fortfarande på att byggas.',
    noCategoriesFound: 'Inga kategorier hittades',
    noCategoriesDescription: 'Webbplatskartan byggs för närvarande. Kom tillbaka senare.',
    somethingWentWrong: 'Något gick fel',
    errorLoadingCategory: 'Vi stötte på ett fel vid inläsning av webbplatskartans kategori.',
    tryAgain: 'Försök igen',
    exploreDescription:
      'Utforska vår omfattande katalog organiserad i kategorier och underkategorier. Hitta exakt det du söker i vår strukturerade webbplatskarta.',
    clickToExplore: 'Klicka på valfri kategori för att utforska dess underkategorier och produkter',
    debugInfo: 'Felsökningsinfo (Utveckling)',
    sitemapCategory: 'Webbplatskartekategori',
    comprehensiveSitemapDescription: 'Bläddra i vår omfattande webbplatskartestruktur.',
    categoryNotFound: 'Kategori hittades inte',
    categoryNotFoundDescription: 'Den begärda webbplatskartekategorin kunde inte hittas.',
    completeCategoryStructure: 'Fullständig kategoristruktur',
    navigateSubcategories: 'Navigera genom underkategorier',
  },

  // Polish
  pl: {
    expandAll: 'Rozwiń wszystko',
    collapseAll: 'Zwiń wszystko',
    expand: 'Rozwiń',
    collapse: 'Zwiń',
    categories: 'Kategorie',
    maxDepth: 'Maks. głębokość',
    mainCategories: 'Główne kategorie',
    totalSubcategories: 'Łączna liczba podkategorii',
    products: 'Produkty',
    empty: 'Pusto',
    explore: 'Przeglądaj',
    backToSitemap: 'Powrót do mapy strony',
    siteNavigation: 'Nawigacja strony',
    deepStructure: 'Głęboka struktura',
    noSubcategoriesFound: 'Nie znaleziono podkategorii',
    noSubcategoriesDescription:
      'Ta kategoria nie ma jeszcze podkategorii. Może to być kategoria produktowa lub jest w trakcie tworzenia.',
    noCategoriesFound: 'Nie znaleziono kategorii',
    noCategoriesDescription: 'Mapa strony jest obecnie w trakcie budowy. Prosimy wrócić później.',
    somethingWentWrong: 'Coś poszło nie tak',
    errorLoadingCategory: 'Wystąpił błąd podczas ładowania kategorii mapy strony.',
    tryAgain: 'Spróbuj ponownie',
    exploreDescription:
      'Przeglądaj nasz kompleksowy katalog uporządkowany według kategorii i podkategorii. Znajdź dokładnie to, czego szukasz w naszej uporządkowanej mapie strony.',
    clickToExplore: 'Kliknij dowolną kategorię, aby przeglądać jej podkategorie i produkty',
    debugInfo: 'Informacje diagnostyczne (Deweloper)',
    sitemapCategory: 'Kategoria mapy strony',
    comprehensiveSitemapDescription: 'Przeglądaj naszą pełną strukturę mapy strony.',
    categoryNotFound: 'Nie znaleziono kategorii',
    categoryNotFoundDescription: 'Żądana kategoria mapy strony nie została znaleziona.',
    completeCategoryStructure: 'Pełna struktura kategorii',
    navigateSubcategories: 'Przeglądaj podkategorie',
  },

  // Catalan
  ca: {
    expandAll: 'Expandir-ho tot',
    collapseAll: 'Reduir-ho tot',
    expand: 'Expandir',
    collapse: 'Reduir',
    categories: 'Categories',
    maxDepth: 'Profunditat màx.',
    mainCategories: 'Categories principals',
    totalSubcategories: 'Total de subcategories',
    products: 'Productes',
    empty: 'Buit',
    explore: 'Explorar',
    backToSitemap: 'Tornar al mapa del lloc',
    siteNavigation: 'Navegació del lloc',
    deepStructure: 'Estructura profunda',
    noSubcategoriesFound: 'No s\'han trobat subcategories',
    noSubcategoriesDescription:
      'Aquesta categoria encara no té subcategories. Podria ser una categoria de productes o estar encara en construcció.',
    noCategoriesFound: 'No s\'han trobat categories',
    noCategoriesDescription: 'El mapa del lloc s\'està construint actualment. Torneu-hi més tard.',
    somethingWentWrong: 'Alguna cosa ha anat malament',
    errorLoadingCategory: 'Hem trobat un error en carregar la categoria del mapa del lloc.',
    tryAgain: 'Tornar-ho a provar',
    exploreDescription:
      'Exploreu el nostre catàleg complet organitzat en categories i subcategories. Trobeu exactament el que cerqueu al nostre mapa del lloc estructurat.',
    clickToExplore: 'Feu clic a qualsevol categoria per explorar les seves subcategories i productes',
    debugInfo: 'Informació de depuració (Desenvolupament)',
    sitemapCategory: 'Categoria del mapa del lloc',
    comprehensiveSitemapDescription: 'Navegueu per la nostra estructura completa del mapa del lloc.',
    categoryNotFound: 'Categoria no trobada',
    categoryNotFoundDescription: 'No s\'ha pogut trobar la categoria del mapa del lloc sol·licitada.',
    completeCategoryStructure: 'Estructura completa de categories',
    navigateSubcategories: 'Navegar per les subcategories',
  },

  // Irish
  ga: {
    expandAll: 'Leathnaigh Gach Rud',
    collapseAll: 'Laghdaigh Gach Rud',
    expand: 'Leathnaigh',
    collapse: 'Laghdaigh',
    categories: 'Catagóirí',
    maxDepth: 'Uasdoimhneacht',
    mainCategories: 'Príomhchatagóirí',
    totalSubcategories: 'Fo-chatagóirí Iomlána',
    products: 'Táirgí',
    empty: 'Folamh',
    explore: 'Féach',
    backToSitemap: 'Ar ais go Léarscáil an tSuímh',
    siteNavigation: 'Nascleanúint an tSuímh',
    deepStructure: 'Struchtúr domhain',
    noSubcategoriesFound: 'Níor aimsíodh aon fho-chatagóirí',
    noSubcategoriesDescription:
      'Níl aon fho-chatagóirí ag an gcatagóir seo fós. D\'fhéadfadh sé gur catagóir táirgí í nó go bhfuil sí á tógáil go fóill.',
    noCategoriesFound: 'Níor aimsíodh aon chatagóirí',
    noCategoriesDescription: 'Tá léarscáil an tsuímh á tógáil faoi láthair. Tar ar ais níos déanaí.',
    somethingWentWrong: 'Chuaigh rud éigin mícheart',
    errorLoadingCategory: 'Tharla earráid agus catagóir léarscáil an tsuímh á lódáil.',
    tryAgain: 'Bain triail eile as',
    exploreDescription:
      'Féach ar ár gcatalóg cuimsitheach arna eagrú i gcatagóirí agus fo-chatagóirí. Aimsigh go díreach an rud atá á lorg agat inár léarscáil struchtúrtha suímh.',
    clickToExplore: 'Cliceáil ar aon chatagóir chun a fo-chatagóirí agus a táirgí a fheiceáil',
    debugInfo: 'Eolas Dífhabhtaithe (Forbairt)',
    sitemapCategory: 'Catagóir Léarscáil an tSuímh',
    comprehensiveSitemapDescription: 'Brabhsáil ár struchtúr cuimsitheach léarscáil an tsuímh.',
    categoryNotFound: 'Catagóir Gan Aimsiú',
    categoryNotFoundDescription: 'Níorbh fhéidir an chatagóir léarscáil an tsuímh a iarradh a aimsiú.',
    completeCategoryStructure: 'Struchtúr Iomlán na gCatagóirí',
    navigateSubcategories: 'Nascleanúint trí fho-chatagóirí',
  },

  // Dutch
  nl: {
    expandAll: 'Alles uitklappen',
    collapseAll: 'Alles inklappen',
    expand: 'Uitklappen',
    collapse: 'Inklappen',
    categories: 'Categorieën',
    maxDepth: 'Max. diepte',
    mainCategories: 'Hoofdcategorieën',
    totalSubcategories: 'Totaal subcategorieën',
    products: 'Producten',
    empty: 'Leeg',
    explore: 'Verkennen',
    backToSitemap: 'Terug naar siteoverzicht',
    siteNavigation: 'Sitenavigatie',
    deepStructure: 'Diepe structuur',
    noSubcategoriesFound: 'Geen subcategorieën gevonden',
    noSubcategoriesDescription:
      'Deze categorie heeft nog geen subcategorieën. Het kan een productcategorie zijn of is nog in opbouw.',
    noCategoriesFound: 'Geen categorieën gevonden',
    noCategoriesDescription: 'Het siteoverzicht wordt momenteel opgebouwd. Kom later terug.',
    somethingWentWrong: 'Er is iets misgegaan',
    errorLoadingCategory: 'Er is een fout opgetreden bij het laden van de siteoverzichtcategorie.',
    tryAgain: 'Opnieuw proberen',
    exploreDescription:
      'Verken onze uitgebreide catalogus, georganiseerd in categorieën en subcategorieën. Vind precies wat u zoekt in ons gestructureerde siteoverzicht.',
    clickToExplore: 'Klik op een categorie om de subcategorieën en producten te verkennen',
    debugInfo: 'Foutopsporingsinformatie (Ontwikkeling)',
    sitemapCategory: 'Siteoverzichtcategorie',
    comprehensiveSitemapDescription: 'Blader door onze uitgebreide siteoverzichtstructuur.',
    categoryNotFound: 'Categorie niet gevonden',
    categoryNotFoundDescription: 'De gevraagde siteoverzichtcategorie kon niet worden gevonden.',
    completeCategoryStructure: 'Volledige categoriestructuur',
    navigateSubcategories: 'Navigeer door subcategorieën',
  },

  // Tamil
  ta: {
    expandAll: 'அனைத்தையும் விரிவாக்கு',
    collapseAll: 'அனைத்தையும் சுருக்கு',
    expand: 'விரிவாக்கு',
    collapse: 'சுருக்கு',
    categories: 'வகைகள்',
    maxDepth: 'அதிகபட்ச ஆழம்',
    mainCategories: 'முதன்மை வகைகள்',
    totalSubcategories: 'மொத்த துணைவகைகள்',
    products: 'பொருட்கள்',
    empty: 'காலியானது',
    explore: 'ஆராயுங்கள்',
    backToSitemap: 'தள வரைபடத்திற்குத் திரும்பு',
    siteNavigation: 'தள வழிசெலுத்தல்',
    deepStructure: 'ஆழமான கட்டமைப்பு',
    noSubcategoriesFound: 'துணைவகைகள் எதுவும் கிடைக்கவில்லை',
    noSubcategoriesDescription:
      'இந்த வகையில் இன்னும் துணைவகைகள் இல்லை. இது ஒரு பொருள் வகையாக இருக்கலாம் அல்லது இன்னும் உருவாக்கப்பட்டு வருகிறது.',
    noCategoriesFound: 'வகைகள் எதுவும் கிடைக்கவில்லை',
    noCategoriesDescription: 'தள வரைபடம் தற்போது உருவாக்கப்பட்டு வருகிறது. தயவுசெய்து பின்னர் மீண்டும் வாருங்கள்.',
    somethingWentWrong: 'ஏதோ தவறு நடந்துவிட்டது',
    errorLoadingCategory: 'தள வரைபட வகையை ஏற்றும்போது பிழை ஏற்பட்டது.',
    tryAgain: 'மீண்டும் முயற்சிக்கவும்',
    exploreDescription:
      'வகைகள் மற்றும் துணைவகைகளாக ஒழுங்கமைக்கப்பட்ட எங்கள் விரிவான பட்டியலை ஆராயுங்கள். எங்கள் கட்டமைக்கப்பட்ட தள வரைபடத்தில் நீங்கள் தேடுவதை சரியாகக் கண்டறியுங்கள்.',
    clickToExplore: 'எந்த வகையையும் கிளிக் செய்து அதன் துணைவகைகள் மற்றும் பொருட்களை ஆராயுங்கள்',
    debugInfo: 'பிழைத்திருத்தத் தகவல் (மேம்பாடு)',
    sitemapCategory: 'தள வரைபட வகை',
    comprehensiveSitemapDescription: 'எங்கள் விரிவான தள வரைபட கட்டமைப்பை உலாவுங்கள்.',
    categoryNotFound: 'வகை கிடைக்கவில்லை',
    categoryNotFoundDescription: 'கோரப்பட்ட தள வரைபட வகை கண்டுபிடிக்க முடியவில்லை.',
    completeCategoryStructure: 'முழுமையான வகை கட்டமைப்பு',
    navigateSubcategories: 'துணைவகைகள் வழியாக செல்லுங்கள்',
  },

  // Galician
  gl: {
    expandAll: 'Expandir todo',
    collapseAll: 'Contraer todo',
    expand: 'Expandir',
    collapse: 'Contraer',
    categories: 'Categorías',
    maxDepth: 'Profundidade máx.',
    mainCategories: 'Categorías principais',
    totalSubcategories: 'Total de subcategorías',
    products: 'Produtos',
    empty: 'Baleiro',
    explore: 'Explorar',
    backToSitemap: 'Volver ao mapa do sitio',
    siteNavigation: 'Navegación do sitio',
    deepStructure: 'Estrutura profunda',
    noSubcategoriesFound: 'Non se atoparon subcategorías',
    noSubcategoriesDescription:
      'Esta categoría aínda non ten subcategorías. Pode ser unha categoría de produtos ou aínda está en construción.',
    noCategoriesFound: 'Non se atoparon categorías',
    noCategoriesDescription: 'O mapa do sitio está a construírse actualmente. Volva máis tarde.',
    somethingWentWrong: 'Algo saíu mal',
    errorLoadingCategory: 'Atopamos un erro ao cargar a categoría do mapa do sitio.',
    tryAgain: 'Tentar de novo',
    exploreDescription:
      'Explore o noso catálogo completo organizado en categorías e subcategorías. Atope exactamente o que procura no noso mapa do sitio estruturado.',
    clickToExplore: 'Prema en calquera categoría para explorar as súas subcategorías e produtos',
    debugInfo: 'Info de depuración (Desenvolvemento)',
    sitemapCategory: 'Categoría do mapa do sitio',
    comprehensiveSitemapDescription: 'Navegue pola nosa estrutura completa do mapa do sitio.',
    categoryNotFound: 'Categoría non atopada',
    categoryNotFoundDescription: 'A categoría do mapa do sitio solicitada non se puido atopar.',
    completeCategoryStructure: 'Estrutura completa de categorías',
    navigateSubcategories: 'Navegar polas subcategorías',
  },

  // Welsh
  cy: {
    // Main navigation buttons
    expandAll: 'Ehangu Popeth',
    collapseAll: 'Crebachur Popeth',
    expand: 'Ehangu',
    collapse: 'Crebachur',

    // Labels and counts
    categories: 'Categorïau',
    maxDepth: 'Dyfnder Uchaf',
    mainCategories: 'Prif Gategorïau',
    totalSubcategories: 'Cyfanswm Is-gategorïau',
    products: 'Cynhyrchion',
    empty: 'Gwag',
    explore: 'Archwilio',

    // Navigation
    backToSitemap: "Yn ôl i Fap y Wefan",
    siteNavigation: "Llywio'r Wefan",

    // Status indicators
    deepStructure: 'Strwythur dwfn',

    // Empty states
    noSubcategoriesFound: 'Ni chanfuwyd is-gategorïau',
    noSubcategoriesDescription:
      "Nid oes gan y categori hwn unrhyw is-gategorïau eto. Gallai fod yn gategori cynhyrchion neu'n dal i gael ei adeiladu.",
    noCategoriesFound: 'Ni chanfuwyd categorïau',
    noCategoriesDescription: "Mae map y wefan yn cael ei adeiladu ar hyn o bryd. Dewch yn ôl yn ddiweddarach os gwelwch yn dda.",

    // Error states
    somethingWentWrong: 'Aeth rhywbeth o le',
    errorLoadingCategory: "Cawsom wall wrth lwytho categori map y wefan.",
    tryAgain: 'Rhowch gynnig arall arni',

    // Descriptions and hints
    exploreDescription:
      "Archwiliwch ein catalog cynhwysfawr wedi'i drefnu yn ôl categorïau ac is-gategorïau. Dewch o hyd i'r union beth rydych yn chwilio amdano yn ein map gwefan strwythuredig.",
    clickToExplore: "Cliciwch ar unrhyw gategori i archwilio ei is-gategorïau a'i gynhyrchion",

    // Development
    debugInfo: 'Gwybodaeth Ddadfygio (Datblygu)',

    // Metadata
    sitemapCategory: 'Categori Map y Wefan',
    comprehensiveSitemapDescription: 'Porwch drwy strwythur cynhwysfawr ein map gwefan.',
    categoryNotFound: 'Ni Chanfuwyd y Categori',
    categoryNotFoundDescription: "Nid oedd modd dod o hyd i gategori'r map gwefan y gofynnwyd amdano.",
    completeCategoryStructure: 'Strwythur Categorïau Cyflawn',
    navigateSubcategories: "Llywio drwy'r is-gategorïau",
  },
  // Bengali
  bn: {
    expandAll: 'সব প্রসারিত করুন',
    collapseAll: 'সব সংকুচিত করুন',
    expand: 'প্রসারিত করুন',
    collapse: 'সংকুচিত করুন',
    categories: 'বিভাগসমূহ',
    maxDepth: 'সর্বোচ্চ গভীরতা',
    mainCategories: 'প্রধান বিভাগসমূহ',
    totalSubcategories: 'মোট উপবিভাগ',
    products: 'পণ্যসমূহ',
    empty: 'খালি',
    explore: 'অন্বেষণ করুন',
    backToSitemap: 'সাইটম্যাপে ফিরে যান',
    siteNavigation: 'সাইট নেভিগেশন',
    deepStructure: 'গভীর কাঠামো',
    noSubcategoriesFound: 'কোনো উপবিভাগ পাওয়া যায়নি',
    noSubcategoriesDescription:
      'এই বিভাগে এখনও কোনো উপবিভাগ নেই। এটি একটি পণ্য বিভাগ হতে পারে অথবা এখনও তৈরি হচ্ছে।',
    noCategoriesFound: 'কোনো বিভাগ পাওয়া যায়নি',
    noCategoriesDescription: 'সাইটম্যাপটি বর্তমানে তৈরি হচ্ছে। অনুগ্রহ করে পরে আবার দেখুন।',
    somethingWentWrong: 'কিছু একটা সমস্যা হয়েছে',
    errorLoadingCategory: 'সাইটম্যাপ বিভাগ লোড করার সময় একটি ত্রুটি হয়েছে।',
    tryAgain: 'আবার চেষ্টা করুন',
    exploreDescription:
      'বিভাগ ও উপবিভাগে সাজানো আমাদের বিস্তৃত ক্যাটালগ অন্বেষণ করুন। আমাদের কাঠামোবদ্ধ সাইটম্যাপে আপনি যা খুঁজছেন ঠিক তা খুঁজে পান।',
    clickToExplore: 'যেকোনো বিভাগে ক্লিক করে এর উপবিভাগ ও পণ্যসমূহ অন্বেষণ করুন',
    debugInfo: 'ডিবাগ তথ্য (ডেভেলপমেন্ট)',
    sitemapCategory: 'সাইটম্যাপ বিভাগ',
    comprehensiveSitemapDescription: 'আমাদের বিস্তৃত সাইটম্যাপ কাঠামো ব্রাউজ করুন।',
    categoryNotFound: 'বিভাগ পাওয়া যায়নি',
    categoryNotFoundDescription: 'অনুরোধকৃত সাইটম্যাপ বিভাগটি খুঁজে পাওয়া যায়নি।',
    completeCategoryStructure: 'সম্পূর্ণ বিভাগ কাঠামো',
    navigateSubcategories: 'উপবিভাগসমূহে নেভিগেট করুন',
  },

  // Basque
  eu: {
    expandAll: 'Zabaldu dena',
    collapseAll: 'Tolestu dena',
    expand: 'Zabaldu',
    collapse: 'Tolestu',
    categories: 'Kategoriak',
    maxDepth: 'Sakonera max.',
    mainCategories: 'Kategoria nagusiak',
    totalSubcategories: 'Azpikategoria guztira',
    products: 'Produktuak',
    empty: 'Hutsik',
    explore: 'Esploratu',
    backToSitemap: 'Itzuli gunearen mapara',
    siteNavigation: 'Gunearen nabigazioa',
    deepStructure: 'Egitura sakona',
    noSubcategoriesFound: 'Ez da azpikategoriarik aurkitu',
    noSubcategoriesDescription:
      'Kategoria honek oraindik ez du azpikategoriarik. Produktu-kategoria bat izan daiteke edo oraindik eraikitzen ari da.',
    noCategoriesFound: 'Ez da kategoriarik aurkitu',
    noCategoriesDescription: 'Gunearen mapa eraikitzen ari da une honetan. Mesedez, itzuli geroago.',
    somethingWentWrong: 'Zerbait gaizki joan da',
    errorLoadingCategory: 'Errorea gertatu da gunearen maparen kategoria kargatzean.',
    tryAgain: 'Saiatu berriro',
    exploreDescription:
      'Esploratu gure katalogo osoa kategoriatan eta azpikategoriatan antolatua. Aurkitu zehazki bilatzen ari zarena gure gune-mapa egituratuan.',
    clickToExplore: 'Egin klik edozein kategoriatan azpikategoriak eta produktuak esploratzeko',
    debugInfo: 'Arazketa-informazioa (Garapena)',
    sitemapCategory: 'Gune-maparen kategoria',
    comprehensiveSitemapDescription: 'Arakatu gure gune-maparen egitura osoa.',
    categoryNotFound: 'Kategoria ez da aurkitu',
    categoryNotFoundDescription: 'Eskatutako gune-maparen kategoria ezin izan da aurkitu.',
    completeCategoryStructure: 'Kategorien egitura osoa',
    navigateSubcategories: 'Nabigatu azpikategorien artean',
  },

  // Romansh
  rm: {
    expandAll: 'Expander tut',
    collapseAll: 'Reducir tut',
    expand: 'Expander',
    collapse: 'Reducir',
    categories: 'Categorias',
    maxDepth: 'Profunditad max.',
    mainCategories: 'Categorias principalas',
    totalSubcategories: 'Total da sutcategorias',
    products: 'Products',
    empty: 'Vid',
    explore: 'Explorar',
    backToSitemap: 'Enavos a la carta dal lieu',
    siteNavigation: 'Navigaziun dal lieu',
    deepStructure: 'Structura profunda',
    noSubcategoriesFound: 'Naginas sutcategorias chattadas',
    noSubcategoriesDescription:
      'Questa categoria n\'ha anc naginas sutcategorias. Ella pudess esser ina categoria da products u è anc en construcziun.',
    noCategoriesFound: 'Naginas categorias chattadas',
    noCategoriesDescription: 'La carta dal lieu vegn actualmain construida. Turnai pli tard.',
    somethingWentWrong: 'Insatge è ì mal',
    errorLoadingCategory: 'Nus avain constatà ina errur cun chargiar la categoria da la carta dal lieu.',
    tryAgain: 'Empruvar anc ina giada',
    exploreDescription:
      'Explorai noss catalog cumplet organisà en categorias e sutcategorias. Chattai exactamain quai che Vus tschertgais en nossa carta dal lieu structurada.',
    clickToExplore: 'Clitgai sin ina categoria per explorar sias sutcategorias e products',
    debugInfo: 'Infurmaziuns da debugging (Svilup)',
    sitemapCategory: 'Categoria da la carta dal lieu',
    comprehensiveSitemapDescription: 'Navigai tras nossa structura cumpletta da la carta dal lieu.',
    categoryNotFound: 'Categoria betg chattada',
    categoryNotFoundDescription: 'La categoria dumandada da la carta dal lieu n\'ha betg pudì vegnir chattada.',
    completeCategoryStructure: 'Structura cumpletta da las categorias',
    navigateSubcategories: 'Navigar tras las sutcategorias',
  },

  // Marathi
  mr: {
    expandAll: 'सर्व विस्तारित करा',
    collapseAll: 'सर्व संकुचित करा',
    expand: 'विस्तारित करा',
    collapse: 'संकुचित करा',
    categories: 'श्रेण्या',
    maxDepth: 'कमाल खोली',
    mainCategories: 'मुख्य श्रेण्या',
    totalSubcategories: 'एकूण उपश्रेण्या',
    products: 'उत्पादने',
    empty: 'रिक्त',
    explore: 'शोधा',
    backToSitemap: 'साइटमॅपवर परत जा',
    siteNavigation: 'साइट नेव्हिगेशन',
    deepStructure: 'खोल रचना',
    noSubcategoriesFound: 'उपश्रेण्या आढळल्या नाहीत',
    noSubcategoriesDescription:
      'या श्रेणीमध्ये अद्याप उपश्रेण्या नाहीत. ही उत्पादन श्रेणी असू शकते किंवा अद्याप तयार होत आहे.',
    noCategoriesFound: 'श्रेण्या आढळल्या नाहीत',
    noCategoriesDescription: 'साइटमॅप सध्या तयार होत आहे. कृपया नंतर पुन्हा तपासा.',
    somethingWentWrong: 'काहीतरी चूक झाली',
    errorLoadingCategory: 'साइटमॅप श्रेणी लोड करताना त्रुटी आली.',
    tryAgain: 'पुन्हा प्रयत्न करा',
    exploreDescription:
      'श्रेण्या आणि उपश्रेण्यांमध्ये व्यवस्थित केलेला आमचा सर्वसमावेशक कॅटलॉग शोधा. आमच्या संरचित साइटमॅपमध्ये तुम्हाला हवे ते अचूक शोधा.',
    clickToExplore: 'उपश्रेण्या आणि उत्पादने शोधण्यासाठी कोणत्याही श्रेणीवर क्लिक करा',
    debugInfo: 'डीबग माहिती (विकास)',
    sitemapCategory: 'साइटमॅप श्रेणी',
    comprehensiveSitemapDescription: 'आमची सर्वसमावेशक साइटमॅप रचना ब्राउझ करा.',
    categoryNotFound: 'श्रेणी आढळली नाही',
    categoryNotFoundDescription: 'विनंती केलेली साइटमॅप श्रेणी सापडली नाही.',
    completeCategoryStructure: 'संपूर्ण श्रेणी रचना',
    navigateSubcategories: 'उपश्रेण्यांमधून नेव्हिगेट करा',
  },
  // Māori
  mi: {
    expandAll: 'Whakawhānui Katoa',
    collapseAll: 'Whakangaro Katoa',
    expand: 'Whakawhānui',
    collapse: 'Whakangaro',
    categories: 'Ngā Wāhanga',
    maxDepth: 'Hōhonu Teitei',
    mainCategories: 'Ngā Wāhanga Matua',
    totalSubcategories: 'Tapeke Wāhanga Iti',
    products: 'Ngā Hua',
    empty: 'Kau',
    explore: 'Tūhura',
    backToSitemap: 'Hoki ki te Mahere Pae',
    siteNavigation: 'Whakatere Pae',
    deepStructure: 'Hanganga hōhonu',
    noSubcategoriesFound: 'Kāore he wāhanga iti i kitea',
    noSubcategoriesDescription:
      'Kāore anō he wāhanga iti o tēnei wāhanga. He wāhanga hua pea, kāore rānei kia oti te hanga.',
    noCategoriesFound: 'Kāore he wāhanga i kitea',
    noCategoriesDescription: 'Kei te hangaia te mahere pae i tēnei wā. Tēnā, hoki mai ā muri.',
    somethingWentWrong: 'I hapa tētahi mea',
    errorLoadingCategory: 'I tūtaki mātou ki tētahi hapa i te utaina o te wāhanga mahere pae.',
    tryAgain: 'Whakamātau anō',
    exploreDescription:
      'Tūhurahia tā mātou kohinga whānui kua whakaritea ki ngā wāhanga me ngā wāhanga iti. Kimihia tika tonu te mea e rapu ana koe i tō mātou mahere pae whakaraupapa.',
    clickToExplore: 'Pāwhiria tētahi wāhanga kia tūhura i ōna wāhanga iti me ngā hua',
    debugInfo: 'Mōhiohio Patuiro (Whanaketanga)',
    sitemapCategory: 'Wāhanga Mahere Pae',
    comprehensiveSitemapDescription: 'Tirotirohia tō mātou hanganga mahere pae whānui.',
    categoryNotFound: 'Kāore i Kitea te Wāhanga',
    categoryNotFoundDescription: 'Kāore i kitea te wāhanga mahere pae i tonoa.',
    completeCategoryStructure: 'Hanganga Wāhanga Katoa',
    navigateSubcategories: 'Whakatere mā ngā wāhanga iti',
  },

  // Telugu
  te: {
    expandAll: 'అన్నీ విస్తరించు',
    collapseAll: 'అన్నీ కుదించు',
    expand: 'విస్తరించు',
    collapse: 'కుదించు',
    categories: 'వర్గాలు',
    maxDepth: 'గరిష్ట లోతు',
    mainCategories: 'ప్రధాన వర్గాలు',
    totalSubcategories: 'మొత్తం ఉపవర్గాలు',
    products: 'ఉత్పత్తులు',
    empty: 'ఖాళీ',
    explore: 'అన్వేషించండి',
    backToSitemap: 'సైట్‌మ్యాప్‌కు తిరిగి వెళ్ళండి',
    siteNavigation: 'సైట్ నావిగేషన్',
    deepStructure: 'లోతైన నిర్మాణం',
    noSubcategoriesFound: 'ఉపవర్గాలు కనుగొనబడలేదు',
    noSubcategoriesDescription:
      'ఈ వర్గంలో ఇంకా ఉపవర్గాలు లేవు. ఇది ఒక ఉత్పత్తి వర్గం కావచ్చు లేదా ఇంకా నిర్మాణంలో ఉంది.',
    noCategoriesFound: 'వర్గాలు కనుగొనబడలేదు',
    noCategoriesDescription: 'సైట్‌మ్యాప్ ప్రస్తుతం నిర్మాణంలో ఉంది. దయచేసి తర్వాత మళ్ళీ చూడండి.',
    somethingWentWrong: 'ఏదో తప్పు జరిగింది',
    errorLoadingCategory: 'సైట్‌మ్యాప్ వర్గాన్ని లోడ్ చేయడంలో లోపం సంభవించింది.',
    tryAgain: 'మళ్ళీ ప్రయత్నించండి',
    exploreDescription:
      'వర్గాలు మరియు ఉపవర్గాలుగా అమర్చబడిన మా సమగ్ర కేటలాగ్‌ను అన్వేషించండి. మా నిర్మాణాత్మక సైట్‌మ్యాప్‌లో మీరు వెతుకుతున్నది సరిగ్గా కనుగొనండి.',
    clickToExplore: 'ఏదైనా వర్గంపై క్లిక్ చేసి దాని ఉపవర్గాలు మరియు ఉత్పత్తులను అన్వేషించండి',
    debugInfo: 'డీబగ్ సమాచారం (అభివృద్ధి)',
    sitemapCategory: 'సైట్‌మ్యాప్ వర్గం',
    comprehensiveSitemapDescription: 'మా సమగ్ర సైట్‌మ్యాప్ నిర్మాణాన్ని బ్రౌజ్ చేయండి.',
    categoryNotFound: 'వర్గం కనుగొనబడలేదు',
    categoryNotFoundDescription: 'అభ్యర్థించిన సైట్‌మ్యాప్ వర్గం కనుగొనబడలేదు.',
    completeCategoryStructure: 'పూర్తి వర్గ నిర్మాణం',
    navigateSubcategories: 'ఉపవర్గాల ద్వారా నావిగేట్ చేయండి',
  },

  // Hindi
  hi: {
    // Main navigation buttons
    expandAll: 'सभी विस्तृत करें',
    collapseAll: 'सभी संक्षिप्त करें',
    expand: 'विस्तृत करें',
    collapse: 'संक्षिप्त करें',

    // Labels and counts
    categories: 'श्रेणियाँ',
    maxDepth: 'अधिकतम गहराई',
    mainCategories: 'मुख्य श्रेणियाँ',
    totalSubcategories: 'कुल उपश्रेणियाँ',
    products: 'उत्पाद',
    empty: 'खाली',
    explore: 'खोजें',

    // Navigation
    backToSitemap: 'साइटमैप पर वापस जाएँ',
    siteNavigation: 'साइट नेविगेशन',

    // Status indicators
    deepStructure: 'गहन संरचना',

    // Empty states
    noSubcategoriesFound: 'कोई उपश्रेणी नहीं मिली',
    noSubcategoriesDescription:
      'इस श्रेणी में अभी कोई उपश्रेणी नहीं है। यह एक उत्पाद श्रेणी हो सकती है या अभी निर्माणाधीन है।',
    noCategoriesFound: 'कोई श्रेणी नहीं मिली',
    noCategoriesDescription: 'साइटमैप वर्तमान में निर्माणाधीन है। कृपया बाद में पुनः देखें।',

    // Error states
    somethingWentWrong: 'कुछ गलत हो गया',
    errorLoadingCategory: 'साइटमैप श्रेणी लोड करते समय एक त्रुटि हुई।',
    tryAgain: 'पुनः प्रयास करें',

    // Descriptions and hints
    exploreDescription:
      'श्रेणियों और उपश्रेणियों में व्यवस्थित हमारे व्यापक कैटलॉग को खोजें। हमारे संरचित साइटमैप में वह सब कुछ खोजें जो आप ढूँढ रहे हैं।',
    clickToExplore: 'उपश्रेणियों और उत्पादों को खोजने के लिए किसी भी श्रेणी पर क्लिक करें',

    // Development
    debugInfo: 'डिबग जानकारी (विकास)',

    // Metadata
    sitemapCategory: 'साइटमैप श्रेणी',
    comprehensiveSitemapDescription: 'हमारे संपूर्ण साइटमैप संरचना को ब्राउज़ करें।',
    categoryNotFound: 'श्रेणी नहीं मिली',
    categoryNotFoundDescription: 'अनुरोधित साइटमैप श्रेणी नहीं मिल सकी।',
    completeCategoryStructure: 'संपूर्ण श्रेणी संरचना',
    navigateSubcategories: 'उपश्रेणियों के माध्यम से नेविगेट करें',
  },

  // Japanese
  ja: {
    // Main navigation buttons
    expandAll: 'すべて展開',
    collapseAll: 'すべて折りたたむ',
    expand: '展開',
    collapse: '折りたたむ',

    // Labels and counts
    categories: 'カテゴリー',
    maxDepth: '最大階層',
    mainCategories: '主要カテゴリー',
    totalSubcategories: 'サブカテゴリー合計',
    products: '商品',
    empty: '空',
    explore: '探索する',

    // Navigation
    backToSitemap: 'サイトマップに戻る',
    siteNavigation: 'サイトナビゲーション',

    // Status indicators
    deepStructure: '深い階層構造',

    // Empty states
    noSubcategoriesFound: 'サブカテゴリーが見つかりません',
    noSubcategoriesDescription:
      'このカテゴリーにはまだサブカテゴリーがありません。商品カテゴリーであるか、現在構築中の可能性があります。',
    noCategoriesFound: 'カテゴリーが見つかりません',
    noCategoriesDescription: 'サイトマップは現在構築中です。後ほど再度ご確認ください。',

    // Error states
    somethingWentWrong: 'エラーが発生しました',
    errorLoadingCategory: 'サイトマップのカテゴリーを読み込む際にエラーが発生しました。',
    tryAgain: '再試行',

    // Descriptions and hints
    exploreDescription:
      'カテゴリーとサブカテゴリーに整理された全カテゴリー一覧をご覧ください。構造化されたサイトマップからお探しの商品を簡単に見つけることができます。',
    clickToExplore: 'カテゴリーをクリックすると、サブカテゴリーや商品を閲覧できます',

    // Development
    debugInfo: 'デバッグ情報（開発用）',

    // Metadata
    sitemapCategory: 'サイトマップ カテゴリー',
    comprehensiveSitemapDescription: 'サイトマップの全体構造をご覧いただけます。',
    categoryNotFound: 'カテゴリーが見つかりません',
    categoryNotFoundDescription: 'ご指定のサイトマップ カテゴリーは見つかりませんでした。',
    completeCategoryStructure: '全カテゴリー構造',
    navigateSubcategories: 'サブカテゴリーを閲覧する',
  },
};

export const sitemapUiContent: Record<LanguageType, SitemapUiContent> =
  proxyLanguageRecord<SitemapUiContent>(sitemapUiContentCore);
